import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import './App.css';
import Hero from './components/Hero';
import ProfileDemoSection from './components/ProfileDemoSection';
import FlexibleDesignSection from './components/FlexibleDesignSection';
import TrustCommunitySection from './components/TrustCommunitySection';
import ProductsPage from './components/ProductsPage';
import LandingDocsPage from './components/LandingDocsPage';
import PricingPage from './components/PricingPage';
import Footer from './components/Footer';
import Navigation from './components/Navigation';
import LoginPage from './components/LoginPage';
import OverviewPage from './components/OverviewPage';
import ApiKeysPage from './components/ApiKeysPage';
import TestingPage from './components/TestingPage';
import AnalyticsPage from './components/AnalyticsPage';
import BillingPage from './components/BillingPage';
import DocsPage from './components/DocsPage';
import ModerationPage from './components/ModerationPage';
import TermsPage from './components/TermsPage';
import PrivacyPage from './components/PrivacyPage';
import InviteOnlyPage from './components/InviteOnlyPage';
import DemoPage from './components/DemoPage';
import ConsoleLayout from './components/ConsoleLayout';
import { INVITE_ONLY_PATH } from './routes';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ApiKeysProvider, useApiKeys } from './contexts/ApiKeysContext';
import { UsageDataProvider } from './contexts/UsageDataContext';
import { BillingProvider } from './contexts/BillingContext';

// Helper to detect if we're on dashboard subdomain
const isDashboardSubdomain = () => {
  const hostname = window.location.hostname;
  return hostname.startsWith('dashboard.') || hostname === 'dashboard.localhost';
};

const getDashboardModerationUrl = () => {
  const { protocol, hostname, port } = window.location;
  const portSegment = port ? `:${port}` : '';

  if (hostname.startsWith('dashboard.') || hostname === 'dashboard.localhost') {
    return '/';
  }

  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.localhost')) {
    return `${protocol}//dashboard.localhost${portSegment}/`;
  }

  if (hostname === 'novembr.dev' || hostname === 'www.novembr.dev' || hostname.endsWith('.novembr.dev')) {
    return 'https://dashboard.novembr.dev/';
  }

  return `${protocol}//dashboard.${hostname}${portSegment}/`;
};

const ConsoleContent: React.FC = () => {
  const { apiKeys } = useApiKeys();
  const location = useLocation();
  
  // Extract page from pathname (e.g., /dashboard/overview -> 'overview' or /overview -> 'overview')
  const getPageFromPath = () => {
    const path = location.pathname;
    if (path === '/dashboard') {
      return 'moderation';
    }
    if (path.startsWith('/dashboard/')) {
      return path.replace('/dashboard/', '');
    }
    // For subdomain, path is just /overview, /analytics, etc.
    if (path.startsWith('/') && path.length > 1) {
      return path.substring(1); // Remove leading /
    }
    return 'moderation';
  };

  const consolePage = getPageFromPath();

  const renderConsolePage = () => {
    switch (consolePage) {
      case 'overview':
        return <OverviewPage apiKeys={apiKeys} />;
      case 'keys':
        return <ApiKeysPage onNavigateToBilling={() => {
          if (isDashboardSubdomain()) {
            window.location.href = '/billing';
          } else {
            window.location.href = '/dashboard/billing';
          }
        }} />;
      case 'testing':
        return <TestingPage />;
      case 'analytics':
        return <AnalyticsPage />;
      case 'billing':
        return <BillingPage />;
      case 'moderation':
        return <ModerationPage />;
      case 'docs':
        return <DocsPage />;
      default:
        return <ModerationPage />;
    }
  };

  const consoleBody = renderConsolePage();
  const legacySidebarPages = ['overview', 'keys', 'testing', 'analytics', 'billing', 'docs'];
  const useLegacyConsoleSidebar = legacySidebarPages.includes(consolePage);

  return (
    <UsageDataProvider>
      <BillingProvider>
        {useLegacyConsoleSidebar ? (
          <ConsoleLayout currentPage={consolePage}>
            {consoleBody}
          </ConsoleLayout>
        ) : (
          consoleBody
        )}
      </BillingProvider>
    </UsageDataProvider>
  );
};

// Protected Route for Dashboard
const ProtectedDashboardRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) {
      navigate(isDashboardSubdomain() ? '/login' : INVITE_ONLY_PATH);
    }
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return <>{children}</>;
};

// Landing Page Component
const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleProductsClick = () => {
    navigate(INVITE_ONLY_PATH);
  };

  const handleLoginClick = () => {
    navigate('/login');
  };

  const handleSignUpClick = () => {
    navigate('/login?mode=signup');
  };

  const handleConsoleClick = () => {
    if (!user) {
      navigate('/login');
      return;
    }

    const moderationUrl = getDashboardModerationUrl();
    if (moderationUrl.startsWith('/')) {
      navigate(moderationUrl);
      return;
    }

    window.location.href = moderationUrl;
  };

  return (
    <div className="App landing-shell">
      <Navigation 
        onLoginClick={handleLoginClick}
        onSignUpClick={handleSignUpClick}
        onConsoleClick={handleConsoleClick}
        onProductsClick={handleProductsClick}
      />
      <Hero onLoginClick={handleSignUpClick} onProductsClick={handleProductsClick} />
      <ProfileDemoSection onLoginClick={handleSignUpClick} />
      <FlexibleDesignSection onLoginClick={handleSignUpClick} onProductsClick={handleProductsClick} />
      <TrustCommunitySection onLoginClick={handleSignUpClick} />
      <Footer />
    </div>
  );
};

// Invite-only Page Component
const InviteOnlyPageWrapper: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="App">
      <Navigation
        onLoginClick={() => navigate(INVITE_ONLY_PATH)}
        onConsoleClick={() => navigate(INVITE_ONLY_PATH)}
        onProductsClick={() => navigate(INVITE_ONLY_PATH)}
      />
      <InviteOnlyPage />
      <Footer />
    </div>
  );
};

// Products Page Component
const ProductsPageWrapper: React.FC = () => {
  const navigate = useNavigate();

  const handleInviteClick = () => {
    navigate(INVITE_ONLY_PATH);
  };

  return (
    <div className="App">
      <Navigation 
        onLoginClick={handleInviteClick} 
        onConsoleClick={() => navigate('/dashboard/moderation')}
        onProductsClick={handleInviteClick}
      />
      <ProductsPage onLoginClick={handleInviteClick} />
      <Footer />
    </div>
  );
};

// Docs Page Component
const DocsPageWrapper: React.FC = () => {
  const navigate = useNavigate();

  const handleInviteClick = () => {
    navigate(INVITE_ONLY_PATH);
  };

  return (
    <div className="App">
      <Navigation 
        onLoginClick={handleInviteClick} 
        onConsoleClick={() => navigate('/dashboard/moderation')}
        onProductsClick={handleInviteClick}
      />
      <LandingDocsPage />
      <Footer />
    </div>
  );
};

// Pricing Page Component
const PricingPageWrapper: React.FC = () => {
  const navigate = useNavigate();

  const handleInviteClick = () => {
    navigate(INVITE_ONLY_PATH);
  };

  return (
    <div className="App">
      <Navigation 
        onLoginClick={handleInviteClick} 
        onConsoleClick={() => navigate('/dashboard/moderation')}
        onProductsClick={() => navigate(INVITE_ONLY_PATH)}
      />
      <PricingPage onLoginClick={handleInviteClick} />
      <Footer />
    </div>
  );
};

// Terms Page Component
const TermsPageWrapper: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="App">
      <Navigation 
        onLoginClick={() => navigate(INVITE_ONLY_PATH)} 
        onConsoleClick={() => navigate('/dashboard/moderation')}
        onProductsClick={() => navigate(INVITE_ONLY_PATH)}
      />
      <TermsPage />
      <Footer />
    </div>
  );
};

// Privacy Page Component
const PrivacyPageWrapper: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="App">
      <Navigation 
        onLoginClick={() => navigate(INVITE_ONLY_PATH)} 
        onConsoleClick={() => navigate('/dashboard/moderation')}
        onProductsClick={() => navigate(INVITE_ONLY_PATH)}
      />
      <PrivacyPage />
      <Footer />
    </div>
  );
};

// Login Page Component
const LoginPageWrapper: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const onDashboardHost = isDashboardSubdomain();

  // Check for signup mode in URL query params
  const searchParams = new URLSearchParams(location.search);
  const mode = searchParams.get('mode') === 'signup' ? 'signup' : 'login';

  useEffect(() => {
    if (user) {
      if (onDashboardHost) {
        navigate('/');
      } else {
        navigate('/dashboard/moderation');
      }
    }
  }, [user, navigate, onDashboardHost]);

  if (onDashboardHost) {
    return <LoginPage onClose={() => navigate('/')} initialMode={mode} />;
  }

  return (
    <div className="App">
      <Navigation
        onLoginClick={() => navigate('/login')}
        onSignUpClick={() => navigate('/login?mode=signup')}
        onConsoleClick={() => navigate('/dashboard/moderation')}
        onProductsClick={() => navigate(INVITE_ONLY_PATH)}
      />
      <LoginPage onClose={() => navigate('/')} initialMode={mode} />
    </div>
  );
};

// Scroll to top on route change
const ScrollToTop: React.FC = () => {
  const location = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  
  return null;
};

// Main App Router
const AppRouter: React.FC = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  // If on dashboard subdomain, show dashboard routes (no /dashboard prefix)
  if (isDashboardSubdomain()) {
    return (
      <>
        <ScrollToTop />
        <Routes>
        <Route path="/login" element={<LoginPageWrapper />} />
        <Route path="/overview" element={
          <ProtectedDashboardRoute>
            <ConsoleContent />
          </ProtectedDashboardRoute>
        } />
        <Route path="/analytics" element={
          <ProtectedDashboardRoute>
            <ConsoleContent />
          </ProtectedDashboardRoute>
        } />
        <Route path="/billing" element={
          <ProtectedDashboardRoute>
            <ConsoleContent />
          </ProtectedDashboardRoute>
        } />
        <Route path="/keys" element={
          <ProtectedDashboardRoute>
            <ConsoleContent />
          </ProtectedDashboardRoute>
        } />
        <Route path="/testing" element={
          <ProtectedDashboardRoute>
            <ConsoleContent />
          </ProtectedDashboardRoute>
        } />
        <Route path="/moderation" element={<Navigate to="/" replace />} />
        <Route path="/" element={
          user ? (
            <ProtectedDashboardRoute>
              <ConsoleContent />
            </ProtectedDashboardRoute>
          ) : (
            <Navigate to="/login" replace />
          )
        } />
        <Route path="*" element={
          user ? <Navigate to="/" replace /> : <Navigate to="/login" replace />
        } />
      </Routes>
      </>
    );
  }

          // Regular routes for main domain
          return (
            <>
              <ScrollToTop />
              <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/products" element={<ProductsPageWrapper />} />
              <Route path="/docs" element={<DocsPageWrapper />} />
              <Route path="/pricing" element={<PricingPageWrapper />} />
              <Route path="/terms" element={<TermsPageWrapper />} />
              <Route path="/privacy" element={<PrivacyPageWrapper />} />
              <Route path={INVITE_ONLY_PATH} element={<InviteOnlyPageWrapper />} />
              <Route path="/demo" element={<DemoPage />} />
              <Route path="/login" element={<LoginPageWrapper />} />
              <Route path="/dashboard" element={
                <ProtectedDashboardRoute>
                  <Navigate to="/dashboard/moderation" replace />
                </ProtectedDashboardRoute>
              } />
              <Route path="/dashboard/moderation" element={
                <ProtectedDashboardRoute>
                  <ConsoleContent />
                </ProtectedDashboardRoute>
              } />
              <Route path="/dashboard/overview" element={
                <ProtectedDashboardRoute>
                  <ConsoleContent />
                </ProtectedDashboardRoute>
              } />
              <Route path="/dashboard/analytics" element={
                <ProtectedDashboardRoute>
                  <ConsoleContent />
                </ProtectedDashboardRoute>
              } />
              <Route path="/dashboard/billing" element={
                <ProtectedDashboardRoute>
                  <ConsoleContent />
                </ProtectedDashboardRoute>
              } />
              <Route path="/dashboard/keys" element={
                <ProtectedDashboardRoute>
                  <ConsoleContent />
                </ProtectedDashboardRoute>
              } />
              <Route path="/dashboard/testing" element={
                <ProtectedDashboardRoute>
                  <ConsoleContent />
                </ProtectedDashboardRoute>
              } />
              <Route path="/dashboard/docs" element={
                <ProtectedDashboardRoute>
                  <ConsoleContent />
                </ProtectedDashboardRoute>
              } />
              <Route path="/dashboard/*" element={
                <ProtectedDashboardRoute>
                  <Navigate to="/dashboard/moderation" replace />
                </ProtectedDashboardRoute>
              } />
            </Routes>
            </>
          );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ApiKeysProvider>
          <AppRouter />
        </ApiKeysProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
